/**
 * SFrame235 — Financial accounts; sources sframe-146 → sframe-144 legacy → planner fallback; state sframe-235.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  extractFinancialAccountsFromSettlementData,
  plannerFinancialFallbackRows,
  type StreamJUnifiedStatus,
} from './streamJSources';
import { RowDocumentPanel } from './streamJRowDocs';

const step = SETTLEMENT_PHASE_CONFIGS.distribute.steps.find((s) => s.id === 'dist-4')!;
const STAT: StreamJUnifiedStatus[] = ['not_started', 'in_progress', 'completed', 'blocked'];
const LAB: Record<StreamJUnifiedStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};
export type xferM = '' | 'tod' | 'beneficiary_designation' | 'probate' | 'pod';

type Exec = {
  status: StreamJUnifiedStatus;
  notes: string;
  blockerReason: string;
  transferMethod: xferM;
  confirmationNumber: string;
};
type Tab = 'all' | StreamJUnifiedStatus;
type FinRow = {
  id: string;
  label: string;
  institution: string;
  accountType: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
  fromPlanner: boolean;
};

function coerce235(raw: unknown): Record<string, Exec> {
  const out: Record<string, Exec> = {};
  if (!raw || typeof raw !== 'object') return out;
  const ex = (raw as { executions?: unknown }).executions;
  if (!ex || typeof ex !== 'object') return out;
  for (const [id, row] of Object.entries(ex)) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const st = String(r.status) as StreamJUnifiedStatus;
    const status = STAT.indexOf(st) >= 0 ? st : ('not_started' as StreamJUnifiedStatus);
    const tm = String(r.transferMethod ?? '');
    const transferMethod =
      tm === 'tod' || tm === 'beneficiary_designation' || tm === 'probate' || tm === 'pod' ? (tm as xferM) : ('' as xferM);
    out[id] = {
      status,
      notes: String(r.notes ?? ''),
      blockerReason: String(r.blockerReason ?? ''),
      transferMethod,
      confirmationNumber: String(r.confirmationNumber ?? ''),
    };
  }
  return out;
}

export default function SFrame235() {
  const { estateId: param } = useParams<{ estateId: string }>();
  const estateId = param ?? null;
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, loadPlannerData } = useSettlementSession(estateId, { readOnly: isArchived });

  const [rows, setRows] = useState<FinRow[]>([]);
  const [exec, setExec] = useState<Record<string, Exec>>({});
  const [tab, setTab] = useState<Tab>('all');
  const [ready, setReady] = useState(false);
  const [plannerBanner, setPlannerBanner] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!estateId) {
      setRows([]);
      setExec({});
      setPlannerBanner(false);
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const [raw146, raw144] = await Promise.all([
        loadFrameData('sframe-146'),
        loadFrameData('sframe-144'),
      ]);
      const d146 = (raw146?.data as Record<string, unknown>) ?? null;
      const d144 = (raw144?.data as Record<string, unknown>) ?? null;
      let accounts: FinRow[] = extractFinancialAccountsFromSettlementData(d146).map((a) => ({
        ...a,
        fromPlanner: false,
      }));

      if (!accounts.length) {
        accounts = extractFinancialAccountsFromSettlementData(d144).map((a) => ({
          ...a,
          fromPlanner: false,
        }));
      }
      accounts = accounts.map((a) => ({ ...a, fromPlanner: false }));
      let planner = false;
      if (!accounts.length) {
        accounts = (await plannerFinancialFallbackRows((id) => loadPlannerData(id))).map((a) => ({
          ...a,
          fromPlanner: true,
        }));
        planner = true;
      }
      const persisted = await loadFrameData('sframe-235');
      if (!cancelled) {
        setPlannerBanner(planner);
        setRows(accounts);
        setExec(coerce235(persisted?.data));
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, loadPlannerData]);

  const gx = useCallback(
    (id: string): Exec =>
      exec[id] ?? {
        status: 'not_started',
        notes: '',
        blockerReason: '',
        transferMethod: '' as xferM,
        confirmationNumber: '',
      },
    [exec]
  );

  const setRow = useCallback(
    (id: string, p: Partial<Exec>) => {
      if (isArchived) return;
      setExec((prev) => {
        const cur =
          prev[id] ??
          ({
            status: 'not_started',
            notes: '',
            blockerReason: '',
            transferMethod: '' as xferM,
            confirmationNumber: '',
          } satisfies Exec);
        return { ...prev, [id]: { ...cur, ...p } };
      });
    },
    [isArchived]
  );

  useEffect(() => {
    if (!estateId || isArchived || !ready) return;
    const tm = window.setTimeout(
      () => void saveFrameData('sframe-235', { version: 1, executions: exec } as Record<string, unknown>),
      520
    );
    return () => clearTimeout(tm);
  }, [exec, estateId, isArchived, ready, saveFrameData]);

  const filtered = useMemo(() => {
    if (tab === 'all') return rows;
    return rows.filter((r) => gx(r.id).status === tab);
  }, [rows, tab, gx]);

  const prog = useMemo(() => {
    const n = rows.length;
    if (!n) return { ok: 0, n };
    const ok = rows.filter((r) => gx(r.id).status === 'completed').length;
    return { ok, n };
  }, [rows, gx]);

  const empty = ready && rows.length === 0;
  const ei = estateId ?? '';

  return (
    <SettlementStepPage
      step={step}
      phase="distribute"
      overviewRoute="/sframe-220"
      plannerFrameIds={['frame-246', 'frame-143']}
      settlementDocumentContext={{ phase: 'distribute', stepId: 'dist-4' }}
    >
      <div className="space-y-4 mb-12">
        <p className="text-sm text-stone-700">
          Rows originate from executor frame <code className="text-xs">sframe-146</code> when tabular payloads exist—otherwise provisional planner balances appear until executors reconcile data.
        </p>
        {plannerBanner && ei && (
          <p className="text-xs text-stone-500">
            Showing planner summaries because settlement frame <code className="text-[11px]">sframe-146</code> did not expose account rows.
          </p>
        )}
        {!empty && (
          <>
            <p className="text-xs uppercase text-stone-500">Transfers completed</p>
            <div className="h-2 max-w-xl rounded-full bg-stone-200 overflow-hidden">
              <div className="h-full bg-blue-900" style={{ width: `${prog.n ? Math.round((prog.ok / prog.n) * 100) : 0}%` }} />
            </div>
            <p className="text-sm">
              {prog.ok}/{prog.n} completed
            </p>
          </>
        )}
        <div className="flex flex-wrap gap-2">
          {(['all'] as Tab[]).concat(STAT).map((k) => (
            <button
              key={String(k)}
              type="button"
              onClick={() => setTab(k)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${tab === k ? 'bg-blue-900 text-white border-blue-950' : 'bg-white border-stone-300'}`}
            >
              {k === 'all' ? 'All' : LAB[k]}
            </button>
          ))}
        </div>
        {empty ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm">
            <p className="font-semibold mb-2">Financial rows missing.</p>
            {ei ? (
              <Link className="text-blue-900 font-semibold underline" to={`/sframe-146/${ei}`}>Jump to Financial Accounts workspace →</Link>
            ) : (
              <span>Select an estate context.</span>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-stone-100 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="py-2 px-2 text-left">Account</th>
                  <th className="py-2 px-2 hidden sm:table-cell">Institution</th>
                  <th className="py-2 px-2 hidden md:table-cell">Type</th>
                  <th className="py-2 px-2 hidden lg:table-cell">Beneficiary hint</th>
                  <th className="py-2 px-2 hidden xl:table-cell">Plan method</th>
                  <th className="py-2 px-2 text-left">Status</th>
                  <th className="py-2 px-2 text-left">Transfer method</th>
                  <th className="py-2 px-2 text-left">Confirmation</th>
                  <th className="py-2 px-2 text-left">Notes</th>
                  <th className="py-2 px-2 text-left">Upload</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const e = gx(r.id);
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-2 px-2">
                          {r.label}
                          {r.fromPlanner ? <span className="block text-[10px] uppercase text-stone-500 mt-1">Planner</span> : null}
                        </td>
                        <td className="py-2 px-2 hidden sm:table-cell">{r.institution}</td>
                        <td className="py-2 px-2 hidden md:table-cell">{r.accountType}</td>
                        <td className="py-2 px-2 hidden lg:table-cell">{r.beneficiaryDisplay}</td>
                        <td className="py-2 px-2 hidden xl:table-cell">{r.distributionMethod}</td>
                        <td className="py-2 px-2">
                          <select
                            className="text-xs border border-stone-200 rounded px-1 py-1 bg-white"
                            disabled={isArchived || !estateId}
                            value={e.status}
                            onChange={(ev) => setRow(r.id, { status: ev.target.value as StreamJUnifiedStatus })}
                          >
                            {STAT.map((s) => (
                              <option key={s} value={s}>{LAB[s]}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-2">
                          <select
                            className="text-[11px] border border-stone-200 rounded px-1 py-1 bg-white max-w-[9rem]"
                            disabled={isArchived || !estateId}
                            value={e.transferMethod}
                            onChange={(ev) => setRow(r.id, { transferMethod: ev.target.value as xferM })}
                          >
                            <option value="">Select</option>
                            <option value="tod">TOD</option>
                            <option value="beneficiary_designation">Beneficiary designation</option>
                            <option value="probate">Probate</option>
                            <option value="pod">Payable on death</option>
                          </select>
                        </td>
                        <td className="py-2 px-2">
                          <input
                            className="w-24 rounded border px-2 py-1 text-xs"
                            disabled={isArchived || !estateId}
                            value={e.confirmationNumber}
                            onChange={(ev) => setRow(r.id, { confirmationNumber: ev.target.value })}
                          />
                        </td>
                        <td className="py-2 px-2 align-top">
                          <textarea
                            className="w-[140px] rounded border px-2 py-1 text-xs min-h-[40px]"
                            disabled={isArchived || !estateId}
                            value={e.notes}
                            onChange={(ev) => setRow(r.id, { notes: ev.target.value })}
                          />
                          {e.status === 'blocked' ? (
                            <textarea
                              className="mt-1 w-[140px] rounded border border-red-100 px-2 py-1 text-xs min-h-[32px]"
                              disabled={isArchived || !estateId}
                              placeholder="Blocker"
                              value={e.blockerReason}
                              onChange={(ev) => setRow(r.id, { blockerReason: ev.target.value })}
                            />
                          ) : null}
                        </td>
                        <td className="py-2 px-2 align-top">
                          <RowDocumentPanel estateId={estateId} phase="distribute" stepId={`exec-235:${r.id}`} readOnly={isArchived} />
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SettlementStepPage>
  );
}
